"use client";

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

// Общие примитивы десктопа. Собраны на токенах `--op-*` (те же, что у консоли
// оператора), потому что визуал будет перерисован отдельно (решение Марата
// 30.07: сначала функционал). Здесь важно, чтобы всё читалось и работало, а не
// чтобы было красиво — красивое делается по работающему.

// Поверхность отделяется от фона ОДНОЙ комбинацией на весь продукт: заливка +
// тонкий кант `--op-card-line` + мягкая тень (референс Timeless, правка Марата
// 05.08 вечером — «как красиво у них отделены блоки»). Кант дорисовывает край
// там, где тень уже растаяла; порознь они дают либо плоско, либо грязно.
//
// Карточки ПЛОТНЫЕ. Стеклянными они были сутки, и это было неверное прочтение:
// прозрачность Марат просил не для карточек, а для подсказки при наведении.
// Стекло живёт ровно в одном месте: `glass` ниже, для всплывающего.
// Радиус r=16 — один на карточки/окна/поля/кнопки во всём продукте (18c,
// «два радиуса»: 16px на всё и пилюля только у чипов-статусов и круглых
// иконок-кнопок). `rounded-2xl` — токен Tailwind ровно под это значение.
export const card =
  "rounded-2xl border border-[var(--op-card-line)] bg-[var(--op-surface)] p-6 [box-shadow:var(--op-shadow)]";

// То же, но с усиленной тенью — для всего, что всплывает ПОВЕРХ затемнения.
export const cardSolid =
  "rounded-2xl border border-[var(--op-card-line)] bg-[var(--op-surface)] p-6 [box-shadow:var(--op-shadow-lg)]";

// Стекло — единственное место, где оно уместно: всплывающая подсказка поверх
// содержимого. Здесь прозрачность работает как эффект, потому что сквозь неё
// видно ровно то, о чём подсказка.
export const glass =
  "rounded-2xl border border-[var(--op-glass-line)] bg-[var(--op-glass)] backdrop-blur-2xl [box-shadow:var(--op-shadow-lg)]";
// Поля — r=16, КАК И карточки/кнопки (18c, «два радиуса»: пилюля осталась
// только у чипов-статусов и круглых иконок-кнопок). До захода «Сезон» поля
// были пилюлями — решение отменено Маратом 18.09: пилюля-поле рядом с
// пилюлей-чипом статуса визуально путались, а сам чип обязан быть единственной
// круглой формой в ряду, чтобы читаться как метка, а не как ещё одно поле.
//
// У textarea свой радиус в px, а не общая переменная, только по историчной
// причине (см. `fieldArea`) — сейчас оба значения совпадают с карточкой.
/* Поле УТОПЛЕНО (решение Марата 05.08 — вариант A): внутренняя тень сверху и
   кант делают из него выемку, в которую пишут. Раньше это была та же серая
   плашка, что и у вторичной кнопки, и отличить поле от кнопки можно было
   только по содержимому. Механика — в `.op-inset` (globals.css). */
export const field =
  "op-inset h-10 w-full rounded-2xl px-4 text-[15px] text-[var(--op-fg)] placeholder:text-[var(--op-dim)] outline-none";
export const fieldArea =
  "op-inset w-full rounded-2xl px-4 py-3 text-[15px] text-[var(--op-fg)] placeholder:text-[var(--op-dim)] outline-none";
/* Выпадающий список — ПОЛЕ СО СВОЕЙ СТРЕЛКОЙ (правка Марата 07.08: «стрелка
   чуть выезжает и некрасиво смотрится»).

   Нативная стрелка браузера рисуется у самого края и на закруглении
   вылезает за угол — на macOS это ещё и своя кнопка со своим фоном.
   Прячем её `appearance-none` и рисуем свою: SVG-шеврон фоном, справа отступ
   под него. Цвет — `--op-muted`, тот же, что у прочих служебных значков. */
export const fieldSelect =
  "op-inset h-10 w-full cursor-pointer appearance-none rounded-2xl bg-[length:16px] bg-[right_14px_center] bg-no-repeat pl-4 pr-10 text-[15px] text-[var(--op-fg)] outline-none";

/**
 * Шеврон раскрытия — ОДИН на весь десктоп (правка Марата 21.09, [17] и [103]:
 * «обрати внимание на все стрелки — они некрасиво выглядят, сильно смещены
 * вправо, и когда нажимаю, как-то некрасиво появляется, нужно с анимацией»).
 *
 * Одно место, один размер 16 px, один цвет служебных значков и один поворот
 * на 180° за 200 мс. Свои инлайновые стрелки по файлам заводить больше не
 * нужно — они и расходились по размеру (14 против 16) и по поведению.
 */
export function Chevron({ open, className = "" }: { open: boolean; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={[
        "h-4 w-4 shrink-0 text-[var(--op-faint)] transition-transform duration-200 ease-[var(--ease-out)]",
        open ? "rotate-180" : "",
        className,
      ].join(" ")}
      aria-hidden
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/** Фон-стрелка отдельным объектом: data-URI в классе Tailwind не переживает. */
export const selectChevron = {
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23808486' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
} as const;

/**
 * Единицы, которыми меряют состав.
 *
 * Один список на все экраны: он был продублирован в карте, калькуляторе и
 * форме новой позиции склада, и стоило добавить единицу в одном месте — в
 * остальных её не было, а расчёт по строке молча становился неполным.
 */
export const UNITS = ["г", "кг", "мл", "л", "шт", "порц", "бут", "упак"] as const;

/**
 * Количество вместе с единицей — ОДНО поле.
 *
 * Правка Марата 08.08: «в разделе „это количество“ всё считается в граммах и
 * нельзя выбрать единицу». Единица была — отдельным полем правее, и человек её
 * там не искал. Это и есть ошибка: количество и единица — одна величина, «120»
 * без «г» не значит ничего, и разносить их по двум пилюлям неправильно.
 *
 * Селект внутри пилюли, а не рядом: пока это две рамки, глаз читает их как два
 * независимых вопроса.
 */
export function QtyUnitField({
  qty,
  unit,
  onQty,
  onUnit,
  placeholder,
  units = UNITS,
  disabled,
  ariaLabel,
}: {
  qty: string;
  unit: string;
  onQty: (v: string) => void;
  onUnit: (v: string) => void;
  placeholder?: string;
  units?: readonly string[];
  disabled?: boolean;
  ariaLabel?: string;
}) {
  return (
    <div className="op-inset op-inset-group flex h-10 items-center rounded-2xl pl-4 pr-1.5">
      <input
        className="min-w-0 flex-1 bg-transparent text-[15px] text-[var(--op-fg)] placeholder:text-[var(--op-dim)] outline-none"
        value={qty}
        onChange={(e) => onQty(e.target.value)}
        inputMode="decimal"
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel ? `${ariaLabel}: количество` : "Количество"}
      />
      <span className="mx-1.5 h-5 w-px shrink-0 bg-[var(--op-line-2)]" />
      <select
        className="h-8 shrink-0 cursor-pointer appearance-none rounded-full bg-transparent bg-[length:14px] bg-[right_6px_center] bg-no-repeat pl-2 pr-6 text-[15px] text-[var(--op-fg)] outline-none"
        style={selectChevron}
        value={unit}
        onChange={(e) => onUnit(e.target.value)}
        disabled={disabled}
        aria-label={ariaLabel ? `${ariaLabel}: единица` : "Единица"}
      >
        {units.map((u) => (
          <option key={u} value={u}>
            {u}
          </option>
        ))}
      </select>
    </div>
  );
}

export const lbl = "t-micro text-[var(--op-faint)] font-medium";

/**
 * Подпись ПОЛЯ ВВОДА.
 *
 * Отличается от `lbl` двумя вещами, и обе — по разбору Марата 08.08.
 *
 * 1. **Не капсом.** `t-micro` — это 11 пикселей ЗАГЛАВНЫМИ с разрядкой:
 *    отличная метка над крупной цифрой, где глаз ведёт число. Над полем ввода
 *    она перетягивает внимание на себя и становится тяжелее самого поля —
 *    «заголовки над пилюлями выпирают». Здесь нужен обычный регистр.
 * 2. **Контрастнее.** Мерили прежний вариант: 3.93:1 при норме 4.5:1 для
 *    мелкого текста, и в длинной форме это читалось как «книзу выцветает».
 *
 * Итог: 13 пикселей обычным регистром на `--op-muted` — читается спокойно и не
 * спорит с полем за внимание.
 */
export const lblField = "block text-[13px] font-medium leading-tight text-[var(--op-muted)]";

/* ─── Переключатель и галочка (правка Марата 13.08) ───

   Системный `<input type="checkbox">` был единственным ненарисованным
   элементом десктопа — и выдавал интерфейс как несделанный. Теперь примитивов
   два, и они РАЗНЫЕ по смыслу:

     Switch   — вкл/выкл ПОВЕДЕНИЯ («Включён», «Начислять премию»);
     Checkbox — отметка ЭЛЕМЕНТА в списке (права, получатели, строки таблицы).

   Смешивать нельзя: тумблер обещает, что что-то начнёт происходить, галочка —
   что элемент попал в выборку.

   Оба построены ВОКРУГ настоящего input, растянутого на весь примитив и
   спрятанного визуально (`appearance-none`): клавиатура, клик по label и
   скринридер работают как у нативного. Видимая часть — соседи по `peer`. */

export function Switch({
  checked,
  onChange,
  disabled,
  ariaLabel,
  className,
  title,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
  title?: string;
}) {
  return (
    <span title={title} className={`relative inline-flex h-6 w-11 shrink-0 ${disabled ? "opacity-40" : ""} ${className ?? ""}`}>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        aria-label={ariaLabel}
        title={title}
        className="peer absolute inset-0 z-10 m-0 h-full w-full cursor-pointer appearance-none rounded-full outline-none disabled:cursor-not-allowed"
      />
      <span
        aria-hidden
        className="absolute inset-0 rounded-full bg-[var(--op-control-line)] transition-colors duration-200 peer-checked:bg-[var(--op-primary)] peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--op-ring)]"
      />
      {/* Шайба едет transform-ом, не left: раскладка не трогается, идёт по GPU.
          Включённый тумблер — ТЁМНЫЙ, не оранжевый (круг 3, S1-А: оранжевый
          только у действий, а тумблер — это состояние). Шайба — цвета текста
          на основной кнопке, чтобы контраст держался в обеих темах. */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ease-[var(--ease-out)] peer-checked:translate-x-5 peer-checked:bg-[var(--op-primary-ink)]"
      />
    </span>
  );
}

export function Checkbox({
  checked,
  onChange,
  disabled,
  ariaLabel,
  className,
  title,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
  title?: string;
}) {
  return (
    <span title={title} className={`relative inline-flex h-[18px] w-[18px] shrink-0 ${disabled ? "opacity-40" : ""} ${className ?? ""}`}>
      {/* Кликабельная область input чуть БОЛЬШЕ видимой коробки (-inset-1):
          18px — цель мельче нормы 2.5.8, растянутый input это лечит бесплатно. */}
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        aria-label={ariaLabel}
        title={title}
        className="peer absolute -inset-1 z-10 m-0 cursor-pointer appearance-none rounded-[6px] outline-none disabled:cursor-not-allowed"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[6px] border border-[var(--op-control-line)] bg-[var(--op-surface)] transition-colors duration-150 peer-checked:border-transparent peer-checked:bg-[var(--op-accent)] peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--op-ring)]"
      />
      {/* Галочка — `--op-accent-ink`: токен заведён ровно под «что читается на
          акцентной заливке» (в светлой теме это белый, в тёмной — графит). */}
      <svg
        aria-hidden
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="pointer-events-none absolute inset-0 m-auto h-3 w-3 text-[var(--op-accent-ink)] opacity-0 transition-opacity duration-150 peer-checked:opacity-100"
      >
        <path d="M4.5 12.5l5 5 10-11" />
      </svg>
    </span>
  );
}

/* ─── Три размера кнопок по значимости (решение Марата 05.08) ───

   Ровно как со шрифтом: три ступени и ничего между. Раньше каждая кнопка
   задавала себе отступы сама, и ряд из четырёх стоял на четырёх разных
   высотах — от этого экран и выглядел мусорным.

   ВЫСОТА ФИКСИРОВАННАЯ (`h-*`), а не «отступы сверху и снизу». Отступы дают
   разную высоту при разной длине подписи и при разном размере значка; ряд
   выравнивается только по заданной высоте.

     L — 48px · 17px · главное действие экрана. Одно на экран, не больше:
              «Сохранить», «Создать», «Войти». Если больших две — значит
              ни одна не главная.
     M — 40px · 15px · рабочая лошадь: вкладки, фильтры, вторичные кнопки,
              круглые иконки. Сюда попадает почти всё.
     S — 32px · 13px · строчное действие внутри карточки или таблицы, где
              кнопка не должна спорить со строкой.

   Класс значимости и размер — разные вещи: `bDanger` (разрушающее) живёт в
   размере M, потому что удаление нажимают из ряда таких же кнопок. */
/* Кнопка ПРИПОДНЯТА: `.op-raise` даёт тень и световую фаску по верхней кромке,
   `active:` вдавливает её обратно. `active:scale` убран — сжатие всей кнопки
   спорит с вдавливанием и вместе они читаются как желе.

   Радиус r=16 (18c, «два радиуса»): подписные кнопки больше не пилюли —
   пилюля осталась только у чипов-статусов (`PillRow`, статусные пилюли) и у
   круглых иконок-кнопок (`iconBase` ниже, форма которых — сам смысл кнопки,
   а не решение о скруглении). */
const btnBase =
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-2xl font-semibold transition-[box-shadow,background-color,color] duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--op-ring)]";

export const sizeL = "h-12 px-6 text-[17px]";
export const sizeM = "h-10 px-5 text-[15px]";
export const sizeS = "h-8 px-3.5 text-[13px]";
/* Четвёртая ступень (19.09). Заведена не от хорошей жизни: пять мест уже
   лепили `${bGhost} !h-7 text-[12px]` поверх размера M, и это не просто
   некрасиво — это СЛОМАНО. Tailwind не разрешает конфликт по порядку слов
   в строке: `px-5` из sizeM и `px-3.5` из переопределения выигрывают друг
   у друга по порядку правил в готовом CSS, а не по тому, что написали
   последним. Отсюда кнопки с высотой 32 и отступами от сорокапиксельной. */
export const sizeXS = "h-7 px-3 text-[12px]";

/* Тёмная кнопка — ПЛОСКАЯ заливка (правка Марата 06.08 вечером): градиентный
   блик по верхней кромке на графите не читался как объём, а выглядел грязным
   пятном. Приподнятость и без него держат тень и световая фаска `.op-raise`.
   На оранжевой градиент ОСТАЁТСЯ — там он работает вместе со свечением. */
export const bPrimary = `${btnBase} ${sizeM} op-raise bg-[var(--op-primary)] text-[var(--op-primary-ink)] hover:bg-[var(--op-primary-hover)]`;
/** Главное действие экрана — единственное. */
export const bPrimaryL = `${btnBase} ${sizeL} op-raise bg-[var(--op-primary)] text-[var(--op-primary-ink)] hover:bg-[var(--op-primary-hover)]`;
/* Акцентная кнопка — главное СОЗИДАЮЩЕЕ действие экрана: «Добавить», «Поставить
   задачу», «Создать тест». Одна на экран, как и тёмная; вместе они не стоят —
   либо оранжевая зовёт начать, либо тёмная завершает (в окнах «Сохранить» —
   тёмная). Светится собственным цветом (`.op-glow-accent`) — единственное
   место, где свечение уместно: это то, ради чего на экран пришли. */
/* НАДПИСЬ НА ОРАНЖЕВОМ — БЕЛАЯ (решение Марата 06.08 вечером, отменяет
   прежний графит). Правило одно на весь десктоп, без исключений: всё, что
   залито акцентом, подписано белым — кнопки, активный кружок рельса, оранжевые
   состояния в графике и задачах, значки дельты.

   ⚠️ Цифры, чтобы решение было принято с открытыми глазами: белый на #F45A1F
   даёт 3.3:1, на тёмной теме (#FF6422) — 3.0:1; графит держал 5.4:1. Марат
   выбрал белый, зная это. Если понадобится и белое, и контраст — затемняем
   заливку КНОПОК до 4.5:1, это один токен.

   Токен `--op-lime-ink` намеренно НЕ трогаем: он же красит консоль оператора
   (в частности головку переключателя), а она — территория второго окна. */
export const bAccent = `${btnBase} ${sizeM} op-glow-accent op-fill-grad bg-[var(--op-lime)] text-white hover:bg-[var(--op-lime-hover)]`;
export const bAccentL = `${btnBase} ${sizeL} op-glow-accent op-fill-grad bg-[var(--op-lime)] text-white hover:bg-[var(--op-lime-hover)]`;
/* ПЛОСКАЯ акцентная — без градиента и свечения (правка Марата 07.08: «кнопки
   выглядят некрасиво, какой-то непонятный градиент»). Градиент задумывался для
   ГЛАВНОЙ кнопки экрана, где он читается как объём; на 150 пикселях в узком
   меню он превращается в грязное пятно поперёк кнопки. Там, где акцент нужен
   как цвет, а не как зов, — эта. */
export const bAccentFlat = `${btnBase} ${sizeM} op-raise bg-[var(--op-lime)] text-white hover:bg-[var(--op-lime-hover)]`;
/* Вторичная кнопка — БЕЛАЯ приподнятая, а не серая плашка: серых кнопок в
   системе больше нет вовсе (вариант A). На карточке она отличается от неё
   тенью и фаской, на фоне страницы — тем же. */
/* 🔴 ГРАНИЦА (19.09). Вторичная кнопка залита `--op-surface` — тем же
   белым, что и карточка, на которой она стоит. Отличала её только тень
   `.op-raise`: 5% чёрного на 1 пиксель и мягкое пятно снизу. На ноутбуке
   при дневном свете этого не видно вовсе, и «Задача словами» читалась как
   подпись, а не как кнопка — пока не наведёшь курсор (Марат, 19.09).

   Кант берём `--op-line-strong` (15% в светлой, 16% в тёмной), а не
   `--op-card-line` (6%): кант карточки специально почти невидим, он
   обводит большую плоскость. Кнопке нужен читаемый контур на 100
   пикселях, иначе мы повторим ту же ошибку тоньше.

   Размеры не едут: `box-sizing: border-box` из preflight держит высоту
   h-10 = 40px вместе с границей. */
export const bGhost = `${btnBase} ${sizeM} op-hover-soft op-raise border border-[var(--op-line-strong)] bg-[var(--op-surface)] text-[var(--op-fg)]`;
export const bDanger = `${btnBase} ${sizeM} op-raise bg-[var(--op-danger-soft)] text-[var(--op-danger-text)] hover:bg-[var(--op-danger)] hover:text-white`;
export const bSmall = `${btnBase} ${sizeS} op-hover-soft op-raise border border-[var(--op-line-strong)] bg-[var(--op-surface)] text-[var(--op-fg)]`;
/** Самая мелкая поверхностная — для плотных рядов внутри строк таблицы. */
export const bTiny = `${btnBase} ${sizeXS} op-hover-soft op-raise border border-[var(--op-line-strong)] bg-[var(--op-surface)] text-[var(--op-fg)]`;
/* Кнопка на фоне страницы (шапка, тулбар раздела) — та же белая приподнятая.
   Отдельным именем оставлена ради читаемости мест вызова. */
export const bSurface = `${btnBase} ${sizeM} op-hover op-raise border border-[var(--op-line-strong)] bg-[var(--op-surface)] text-[var(--op-fg)]`;
/** Третичное действие — без поверхности: подпись, оживающая под курсором. */
export const bQuiet = `${btnBase} ${sizeM} op-quiet text-[var(--op-muted)] hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)]`;
export const bQuietS = `${btnBase} ${sizeS} op-quiet text-[var(--op-muted)] hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)]`;

// Круглые кнопки-иконки — те же три ступени, но квадрат по стороне высоты,
// чтобы стоять в одном ряду с подписными кнопками и не выбиваться.
// `rounded-full` НЕ меняем на r=16 (18c): это и есть исключение «пилюля
// только у чипов-статусов и круглых иконок-кнопок» — форма здесь читается
// как «значок в кружке», а не как радиус поверхности.
const iconBase =
  "grid shrink-0 place-items-center rounded-full transition-[box-shadow,background-color,color] duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--op-ring)]";
export const bIcon = `${iconBase} op-hover-soft op-raise border border-[var(--op-line-strong)] h-10 w-10 bg-[var(--op-surface)] text-[var(--op-muted)] hover:text-[var(--op-fg)]`;
export const bIconS = `${iconBase} op-hover-soft op-raise border border-[var(--op-line-strong)] h-8 w-8 bg-[var(--op-surface)] text-[var(--op-muted)] hover:text-[var(--op-fg)]`;
/** Круглая иконка на фоне страницы — та же белая приподнятая. */
export const bIconSurface = `${iconBase} op-hover op-raise border border-[var(--op-line-strong)] h-10 w-10 bg-[var(--op-surface)] text-[var(--op-muted)] hover:text-[var(--op-fg)]`;
// Залитая тёмным кнопка-иконка светлеет при наведении: оранжевый кант на
// тёмном читается как ошибка, а не как отклик.
export const bIconActive = `${iconBase} op-raise h-10 w-10 bg-[var(--op-primary)] text-[var(--op-primary-ink)] hover:bg-[var(--op-primary-hover)]`;

/* ─── Служебные значки (ревью ui-ux-pro-max §4: vector-only) ───
   Стрелки и крестики были ТЕКСТОВЫМИ глифами «←», «→», «↩», «✕». Глиф зависит
   от шрифта: в Inter он сидит ниже базовой линии, в системном фоллбеке другой
   ширины, и кнопка-стрелка выглядела чуть пьяной. SVG рисуется одинаково
   везде, красится через currentColor и не прыгает.

   ⚠️ ЗНАЧОК ОТВЕЧАЕТ НА КУРСОР (заказ Марата 06.08, расширен вечером: «пусть
   такие же анимации будут везде, где есть значки»). Приём один: кнопка
   получает класс `group`, а сам значок — `motionIcon` и одно
   `group-hover:`-превращение по смыслу действия. Стрелка сдвигается по
   направлению, календарь приоткрывается, отмена отматывает, крестик
   доворачивается. Правила: 150–300мс, только transform, глобальный
   prefers-reduced-motion гасит всё разом. */
export const motionIcon = "transition-transform duration-200 ease-[var(--ease-out)]";
function glyph(path: string) {
  return function GlyphIcon({ className }: { className?: string }) {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className ?? "h-[18px] w-[18px] shrink-0"}
        aria-hidden
      >
        <path d={path} />
      </svg>
    );
  };
}
export const ArrowLeft = glyph("M19 12H5m0 0l6 6m-6-6l6-6");
export const ArrowRight = glyph("M5 12h14m0 0l-6 6m6-6l-6-6");
export const UndoIcon = glyph("M9 14L4 9l5-5M4 9h10a6 6 0 010 12h-3");
export const CloseIcon = glyph("M6 6l12 12M18 6L6 18");

/**
 * Контейнер, который ПЛАВНО ТЯНЕТСЯ под свою начинку.
 *
 * Зачем (правка Марата 08.08: «при переключении Результаты/Тесты само окно
 * дёргается»). Две вкладки почти всегда разной высоты, и при подмене карточка
 * скачком меняет размер — прыгает всё, что ниже на странице.
 *
 * ⚠️ Анимировать высоту «из auto» БРАУЗЕР НЕ УМЕЕТ: нужна числовая точка
 * старта. Первая версия этого не учитывала — отпускала высоту в `auto` после
 * перехода, и следующее переключение шло из «ничего» в число, то есть скачком.
 * Проверил замером: переход показывал 0s.
 *
 * Поэтому порядок здесь строгий и императивный:
 *   1. запомнить высоту ПРЕДЫДУЩЕГО содержимого (`prevH`);
 *   2. на смене — поставить её инлайном без перехода и форсировать reflow;
 *   3. включить переход и поставить новую высоту;
 *   4. по окончании отпустить в `auto`, иначе содержимое, выросшее само
 *      (пришли данные, раскрылась строка), окажется обрезано.
 *
 * Первый показ не анимируется: это появление экрана, а не смена вкладки.
 */
// 260мс = `--dur-base` (словарь токенов §4, было 300 — неотличимо на глаз);
// значение здесь литерал, а не `var(...)`: constant идёт в JS-строку перехода,
// а не в CSS, где custom property резолвился бы сам.
const AUTO_H_MS = 260;

export function AutoHeight({ children, deps }: { children: ReactNode; deps: unknown }) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const prevH = useRef<number | null>(null);
  const firstRun = useRef(true);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const next = inner.offsetHeight;

    if (firstRun.current) {
      firstRun.current = false;
      prevH.current = next;
      return;
    }

    const from = prevH.current;
    prevH.current = next;
    // Нечего анимировать: высота не изменилась или прошлой мы не знаем.
    if (from === null || from === next || next === 0) return;
    // Меньше движения: высота не тянется, содержимое просто подменяется
    // (проявление остаётся у .op-tab-enter внутри).
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    outer.style.transition = "none";
    outer.style.height = `${from}px`;
    void outer.offsetHeight; // форс-reflow: без него обе строки схлопнутся в одну
    outer.style.transition = `height ${AUTO_H_MS}ms var(--op-ease-out)`;
    outer.style.height = `${next}px`;

    const done = setTimeout(() => {
      outer.style.transition = "";
      outer.style.height = "";
    }, AUTO_H_MS + 20);
    return () => clearTimeout(done);
  }, [deps]);

  // Содержимое умеет расти само — догрузились данные, раскрылась строка. Тогда
  // анимировать нечего, но запомнить новую высоту обязательно: иначе следующее
  // переключение стартует из устаревшего числа и дёрнется.
  useEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;
    const ro = new ResizeObserver(() => {
      const h = inner.offsetHeight;
      if (h > 0) prevH.current = h;
    });
    ro.observe(inner);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={outerRef} className="op-auto-h overflow-hidden">
      {/* Смена содержимого — мягкой подменой, а не кадром: высота уже едет, и
          резкая подстановка текста поверх едущей рамки читалась бы рывком. */}
      <div ref={innerRef} key={String(deps)} className="op-tab-enter">
        {children}
      </div>
    </div>
  );
}

export function Card({
  title,
  right,
  children,
  solid,
  className,
}: {
  /** Заголовок карточки. ReactNode, а не строка: рядом со словом бывает
      значок — «🔥 Срочные задачи» собирается тем же примитивом. */
  title?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
  /** Карточка всплывает поверх затемнения — тогда она плотная, а не стеклянная. */
  solid?: boolean;
  /** Дополнительно к примитиву — например `h-full` в выровненной сетке. */
  className?: string;
}) {
  return (
    <section className={`${solid ? cardSolid : card} space-y-4 ${className ?? ""}`}>
      {(title || right) && (
        <header className="flex items-start justify-between gap-3">
          {title && <h2 className="t-body font-semibold text-[var(--op-fg)]">{title}</h2>}
          {right}
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * Место в шапке, куда попадают вкладки раздела.
 *
 * Вкладки объявляет сам раздел — он один знает, какие у него виды и какой
 * сейчас выбран. Но стоять они должны в шапке, слева, в одной строке с
 * локацией (референс Марата 05.08). Тащить состояние вкладок в страницу ради
 * этого не нужно: страница даёт слот, раздел рисует в него порталом.
 */
const TabsSlotContext = createContext<HTMLElement | null>(null);

export function TabsSlot() {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const outer = useContext(TabsSlotProviderContext);
  useEffect(() => {
    outer?.(el);
  }, [el, outer]);
  // Ряд пилюль — СЛЕВА, у края сетки (решение Марата 07.08 после сравнения
  // трёх положений: центр остатка, ось экрана, прижатие вправо — ни одно не
  // подошло). Вкладки начинаются на той же вертикали, что заголовок раздела и
  // карточки под ним.
  return <div ref={setEl} className="flex min-w-0 flex-1 items-center" />;
}

const TabsSlotProviderContext = createContext<((el: HTMLElement | null) => void) | null>(null);

export function TabsSlotProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  return (
    <TabsSlotProviderContext.Provider value={setSlot}>
      <TabsSlotContext.Provider value={slot}>{children}</TabsSlotContext.Provider>
    </TabsSlotProviderContext.Provider>
  );
}

/**
 * Ряд пилюль — выбор одного из нескольких (референс Марата 05.08).
 *
 * Активная залита чёрным, остальные светлые. Не подчёркивание и не рамка:
 * заливка читается с любого расстояния и не спорит со скруглениями вокруг.
 *
 * Одна пилюля РИСУЕТСЯ (правка Марата 06.08 вечером): она подписывает экран, а
 * без неё левая половина шапки стоит пустой — именно это и выглядело
 * недоделанным. Выбора она не даёт, поэтому и нажатие ничего не меняет; это
 * заголовок в форме пилюли, а не панель из одной вкладки.
 *
 * Рисуется в шапку через слот. Пока слот не смонтирован (первый кадр), пилюли
 * не рисуются нигде — мелькание в двух местах хуже, чем их отсутствие на один
 * кадр.
 */
export function Pills<T extends string>({
  items,
  value,
  onChange,
}: {
  items: { key: T; title: string; hint?: string; icon?: ReactNode }[];
  value: T;
  onChange: (key: T) => void;
}) {
  const slot = useContext(TabsSlotContext);
  const rowRef = useRef<HTMLDivElement>(null);
  // Подложка ИЗМЕРЯЕТСЯ по активной кнопке, а не считается по формуле от числа
  // вкладок: подписи разной длины («Сотрудники» против «Роли и доступ»), и
  // формула требовала бы равных колонок — на живом экране это ломало ряд (та же
  // грабля, что у Segmented 06.08).
  const [box, setBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const measure = () => {
      const active = row.querySelector<HTMLElement>('[data-active="true"]');
      if (!active) return;
      setBox({
        left: active.offsetLeft,
        top: active.offsetTop,
        width: active.offsetWidth,
        height: active.offsetHeight,
      });
    };
    measure();
    // Ряд переносится на вторую строку и меняет ширину от темы и шрифта —
    // подложка обязана переехать вместе с кнопкой, а не остаться на старом месте.
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    return () => ro.disconnect();
  }, [value, items, slot]);

  if (items.length === 0 || !slot) return null;
  return createPortal(
    /* Активная вкладка помечается ОДНОЙ ПЕРЕЕЗЖАЮЩЕЙ подложкой (аудит анимаций
       07.08: «с пилюли на пилюлю нет плавной анимации»). Раньше тёмная заливка
       гасла на одной кнопке и загоралась на другой — это читается как «одно
       исчезло, другое появилось». Переезд говорит верное: выбор ОДИН, и он
       сдвинулся. Термин — shared element transition (magic move). */
    // Ряд НЕ переносится, а прокручивается.
    //
    // Слот вкладок в шапке фиксированной высоты: пятая вкладка «Накладные» не
    // влезала в строку десятью пикселями, ряд переносился и наезжал на контент
    // под шапкой. Перенос здесь невозможен по устройству полосы, а подгонять
    // подписи под ширину — это чинить симптом до следующей вкладки.
    //
    // Полоса прокрутки спрятана: горизонтальный скролл делается колесом с
    // Shift и трекпадом, а видимая полоса в шапке — визуальный мусор.
    <div
      ref={rowRef}
      className="op-pill-scroll relative flex flex-nowrap items-center gap-2 overflow-x-auto overflow-y-hidden"
    >
      {box && (
        <span
          aria-hidden
          className="op-pill-track op-raise pointer-events-none absolute rounded-full bg-[var(--op-primary)] transition-[left,top,width,height] duration-[var(--dur-base)]"
          style={{
            left: box.left,
            top: box.top,
            width: box.width,
            height: box.height,
            transitionTimingFunction: "var(--op-ease-out)",
          }}
        />
      )}
      {items.map((it) => {
        const active = it.key === value;
        return (
          <button
            key={it.key}
            data-active={active}
            data-tour={`pill-${it.key}`}
            onClick={() => onChange(it.key)}
            title={it.hint}
            aria-current={active ? "page" : undefined}
            className={[
              // group/pill — чтобы значок вкладки ожил под курсором, как
              // шестерёнка (заказ Марата 06.08 вечером).
              "group/pill relative z-10 inline-flex h-10 shrink-0 items-center gap-2 rounded-full px-5 text-[15px] font-semibold transition-colors duration-200",
              // Поверхность есть только у НЕактивных: у активной её рисует
              // переезжающая подложка, и вторая заливка под ней дала бы двойной
              // тёмный слой на время переезда.
              active
                ? "text-[var(--op-primary-ink)]"
                : "text-[var(--op-muted)] hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)]",
            ].join(" ")}
          >
            {it.icon}
            {it.title}
          </button>
        );
      })}
    </div>,
    slot,
  );
}

/**
 * РЯД ПИЛЮЛЬ С ПЕРЕЕЗЖАЮЩЕЙ ПОДЛОЖКОЙ — один примитив на весь десктоп
 * (правило Марата 07.08: «возьми за правило добавлять её везде, где несколько
 * пилюль рядом»).
 *
 * Зачем отдельный компонент, а не копия разметки в каждом месте. Подложка одна
 * и ЕЗДИТ между вариантами — это и есть смысл: выбор не исчезает и не
 * появляется заново, он сдвигается (shared element / magic move). Собранная
 * руками в пятом месте, такая механика неизбежно разъедется по длительности и
 * кривой, и ряды на соседних экранах начнут вести себя по-разному.
 *
 * Подложка ИЗМЕРЯЕТ активную кнопку, а не считает по формуле от их числа:
 * подписи бывают любой длины («Отметка» против «Ответ текстом»), и формула
 * требовала бы равных колонок — на живом экране от этого сжимаются подписи.
 *
 * `tone` — чем залита подложка: `dark` (выбор режима, по умолчанию) или
 * `accent` (там, где выбор — это состояние «включено», как у диапазона).
 */
export function PillRow<T extends string>({
  items,
  value,
  onChange,
  tone = "dark",
  size = "m",
  wrap = true,
  className,
  ariaLabel,
}: {
  items: { key: T; title: string; hint?: string; icon?: ReactNode }[];
  value: T;
  onChange: (key: T) => void;
  tone?: "dark" | "accent";
  /** m — обычный ряд (36px), s — внутри карточки или панели фильтров (32px). */
  size?: "m" | "s";
  /**
   * Переносить ли ряд на вторую строку. По умолчанию да — в теле страницы это
   * правильно. В ШАПКЕ фиксированной высоты перенос ломает всё: ряд становится
   * выше полосы, и активная пилюля обрезается по её краю (наступил в консоли
   * оператора 08.08). Там ряд обязан ужиматься прокруткой, а не переносом.
   */
  wrap?: boolean;
  className?: string;
  ariaLabel?: string;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const measure = () => {
      const active = row.querySelector<HTMLElement>('[data-active="true"]');
      if (!active) return;
      setBox({
        left: active.offsetLeft,
        top: active.offsetTop,
        width: active.offsetWidth,
        height: active.offsetHeight,
      });
    };
    measure();
    // Ряд переносится на вторую строку при узком окне — подложка обязана
    // переехать вместе с кнопкой, а не остаться на старом месте.
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    return () => ro.disconnect();
  }, [value, items]);

  const h = size === "s" ? "h-8 text-[13px] px-3.5" : "h-9 text-[13px] px-4";

  return (
    <div
      ref={rowRef}
      className={[
        "relative flex items-center gap-1.5",
        // Без переноса ряд уезжает горизонтальной прокруткой. Полоса скрыта:
        // это шапка, а не список, и видимый скроллбар в ней читается ошибкой.
        wrap ? "flex-wrap" : "flex-nowrap overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className ?? "",
      ].join(" ")}
      role="group"
      aria-label={ariaLabel}
    >
      {box && (
        <span
          aria-hidden
          className={[
            "op-pill-track pointer-events-none absolute rounded-full transition-[left,top,width,height] duration-[var(--dur-base)]",
            tone === "accent" ? "op-glow-accent bg-[var(--op-lime)]" : "op-raise bg-[var(--op-primary)]",
          ].join(" ")}
          style={{
            left: box.left,
            top: box.top,
            width: box.width,
            height: box.height,
            transitionTimingFunction: "var(--op-ease-out)",
          }}
        />
      )}
      {items.map((it) => {
        const active = it.key === value;
        return (
          <button
            key={it.key}
            type="button"
            data-active={active}
            onClick={() => onChange(it.key)}
            aria-pressed={active}
            title={it.hint}
            className={[
              "relative z-10 inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full font-semibold transition-colors duration-200",
              h,
              // Поверхность есть только у НЕактивных: активную рисует подложка,
              // и вторая заливка под ней дала бы двойной слой на время переезда.
              active
                ? tone === "accent"
                  ? "text-white"
                  : "text-[var(--op-primary-ink)]"
                : "text-[var(--op-muted)] hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)]",
            ].join(" ")}
          >
            {it.icon}
            {it.title}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Переключатель режима — утоплённая дорожка с ПЕРЕЕЗЖАЮЩЕЙ подсветкой
 * (заказ Марата 06.08 вечером: «когда переходишь 3 дня, неделя и так далее,
 * нет красивой анимации переезда»).
 *
 * Тёмная пилюля здесь одна на всю дорожку и ездит между слотами, а не гаснет в
 * одном месте и загорается в другом. Разница смысловая: переезд говорит «это
 * один и тот же выбор, он сдвинулся», мигание — «одно исчезло, другое
 * появилось». Термин из animation-vocabulary — shared-element (magic move).
 *
 * Подсветка ИЗМЕРЯЕТ активную кнопку, а не считается по формуле от числа
 * элементов. Формула требовала колонок равной ширины — и первая же живая
 * проверка показала, чем это кончается: «3 дня · Неделя · Месяц» в общем ряду
 * сжались, подписи обрезались. Подписи бывают любой длины, поэтому единственная
 * честная ширина — та, что у кнопки на самом деле.
 *
 * Это ВЫБОР РЕЖИМА, а не действие: дорожка утоплена (`op-inset`), как поле.
 */
export function Segmented<T extends string>({
  items,
  value,
  onChange,
  tone = "primary",
  ariaLabel,
}: {
  items: { key: T; title: string; hint?: string }[];
  value: T;
  onChange: (key: T) => void;
  /**
   * Чем закрашена подсветка.
   *
   * `primary` — по умолчанию: тёмная пилюля (в тёмной теме она оранжевая, это
   * сам токен). Годится, когда переключатель на экране один яркий элемент.
   *
   * `accent` — ОРАНЖЕВАЯ пилюля с белым текстом (правка Марата 08.08 про
   * аттестацию: «пусть будет оранжевый… результаты становится оранжевой
   * пилюлей с белым текстом, а так текст чёрный»). Отличается от `primary`
   * тем, что оранжевая В ОБЕИХ темах: `--op-primary` в светлой графитовый, и
   * «оранжевый» получался бы только в тёмной.
   *
   * `surface` — светлая приподнятая шайба, как у переключателя темы. Для мест,
   * где рядом стоит акцентная кнопка и вторая оранжевая заливка была бы лишней.
   */
  tone?: "primary" | "accent" | "surface";
  ariaLabel?: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ left: number; width: number } | null>(null);

  // Измеряем ДО отрисовки кадра (useLayoutEffect): с обычным эффектом
  // подсветка успевала мигнуть в старом месте. Пересчёт — и на смену значения,
  // и на изменение размеров дорожки: подписи и шрифт могут поехать от темы.
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = () => {
      const active = track.querySelector<HTMLElement>('[data-active="true"]');
      if (!active) return;
      setBox({ left: active.offsetLeft, width: active.offsetWidth });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    return () => ro.disconnect();
  }, [value, items]);

  return (
    <div ref={trackRef} className="op-inset relative flex shrink-0 rounded-full p-1" role="group" aria-label={ariaLabel}>
      {/* Пока не измерили — подсветки нет вовсе. Показать её в нуле значило бы
          дать ей проехать через весь переключатель на первом кадре. */}
      {box && (
        <span
          aria-hidden
          className={[
            "op-raise absolute rounded-full transition-[left,width] duration-[260ms]",
            // `--op-elevated` у surface, а не `--op-surface`: дорожка утоплена
            // в `--op-surface-2`, и шайба цвета surface (#1f2224 на #1a1d1f)
            // от неё в тёмной теме почти не отличалась.
            tone === "accent"
              ? "op-glow-accent bg-[var(--op-lime)]"
              : tone === "surface"
                ? "bg-[var(--op-elevated)]"
                : "bg-[var(--op-primary)]",
          ].join(" ")}
          style={{
            top: 4,
            bottom: 4,
            left: box.left,
            width: box.width,
            transitionTimingFunction: "var(--op-ease-out)",
          }}
        />
      )}
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          data-active={value === it.key}
          onClick={() => onChange(it.key)}
          aria-pressed={value === it.key}
          title={it.hint}
          className={[
            "relative z-10 h-8 shrink-0 whitespace-nowrap rounded-full px-3.5 text-[13px] font-semibold transition-colors duration-150",
            value === it.key
              ? tone === "accent"
                ? "text-white"
                : tone === "surface"
                  ? "text-[var(--op-accent)]"
                  : "text-[var(--op-primary-ink)]"
              // У акцентного варианта неактивная подпись — ОСНОВНЫМ цветом, а не
              // приглушённым (Марат: «а так текст чёрный»). Рядом с яркой
              // пилюлей приглушённый серый читался как «недоступно».
              : tone === "accent"
                ? "text-[var(--op-fg)] hover:text-[var(--op-fg)]"
                : "text-[var(--op-muted)] hover:text-[var(--op-fg)]",
          ].join(" ")}
        >
          {it.title}
        </button>
      ))}
    </div>
  );
}

/**
 * Слой поверх всего экрана — подложка для окон и меню.
 *
 * Рисуется ПОРТАЛОМ в корень темы, и это не перестраховка. `position:
 * fixed` отсчитывается от экрана только пока ни у одного предка нет `transform`,
 * `filter` или `backdrop-filter`: любое из них делает предка системой координат
 * для потомков, и окно начинает позиционироваться относительно него. Шапка
 * рабочего места размыта `backdrop-blur`, поэтому окно «Локации», лежавшее
 * внутри неё, растягивалось по высоте шапки: затемнение накрывало одну верхнюю
 * полосу, а верх самого окна уезжал за экран.
 *
 * Портал уносит слой из-под любых таких предков — и правило перестаёт зависеть
 * от того, кто и где применил размытие.
 */
/**
 * Всплывающая панель, привязанная к кнопке.
 *
 * 🔴 Зачем портал, хотя `absolute` короче. Баг Марата 08.08: в «Сравнении»
 * календарь открывался ЗА ЛЕВЫМ КРАЕМ содержимого и уезжал под рельс — из
 * двенадцати месяцев было видно восемь, а стрелка «предыдущий год» пропадала
 * совсем. Причина в том, что `absolute right-0` откладывает панель влево от
 * кнопки, ничего не зная про край экрана, а любой предок с `overflow` её ещё и
 * обрезает. Панель в портале с координатами по вьюпорту не зависит ни от того,
 * ни от другого.
 *
 * Положение считается при открытии и на прокрутке: панель, уехавшая от своей
 * кнопки при скролле, выглядит хуже закрытой.
 */
export function Popover({
  open,
  anchorRef,
  onClose,
  width = 320,
  label,
  children,
}: {
  open: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  width?: number;
  label?: string;
  children: ReactNode;
}) {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; originX: number; below: boolean } | null>(null);
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Сначала выход (120ms), потом onClose: родитель размонтирует уже пустое
  // место. Повторный запрос во время выхода игнорируется.
  const requestClose = useCallback(() => {
    if (closeTimer.current) return;
    setClosing(true);
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setClosing(false);
      onClose();
    }, 130);
  }, [onClose]);
  // Родитель закрыл/открыл сам — сбрасываем незавершённый выход.
  useEffect(() => {
    if (open) return;
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setClosing(false);
  }, [open]);

  useEffect(() => {
    setHost(document.querySelector<HTMLElement>(".op-root") ?? document.body);
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      const a = anchorRef.current?.getBoundingClientRect();
      if (!a) return;
      const gap = 8;
      const pad = 12;
      const h = panelRef.current?.offsetHeight ?? 320;
      // Сторона выбирается по месту, а не задаётся жёстко.
      //
      // По умолчанию панель прижата к ПРАВОМУ краю кнопки — так она растёт
      // внутрь экрана и не лезет в поля. Если слева места не хватает (кнопка
      // стоит у начала строки — ровно случай «Сравнения», где панель уезжала
      // под рельс), раскрываем её ВПРАВО от кнопки. И только если не выходит
      // ни так, ни так, — прижимаем к краю окна.
      const right = a.right - width;
      const left =
        right >= pad
          ? right
          : a.left + width <= window.innerWidth - pad
            ? a.left
            : Math.max(pad, window.innerWidth - width - pad);
      // Снизу, если помещается; иначе над кнопкой. Иначе на нижних строках
      // длинной таблицы календарь открывался бы за нижним краем окна.
      const below = a.bottom + gap;
      const opensBelow = !(below + h > window.innerHeight - pad);
      const top = opensBelow ? below : Math.max(pad, a.top - gap - h);
      // Точка роста — центр кнопки в координатах панели (растёт ОТ кнопки, а
      // не из своего центра); по краям — не ближе 12px, чтобы не «торчать».
      const originX = Math.min(width - 12, Math.max(12, a.left + a.width / 2 - left));
      setPos({ top, left, originX, below: opensBelow });
    };
    place();
    // capture: панель должна ехать и когда прокручивают внутренний контейнер,
    // а не только окно.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, anchorRef, width]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      requestClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, requestClose, anchorRef]);

  if (!open || !host) return null;

  return createPortal(
    <div
      ref={panelRef}
      className={`${cardSolid} op-pop fixed z-[60] p-4`}
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        width,
        // До первого замера панель стоит за экраном: иначе кадр её видно в
        // левом верхнем углу, и открытие читается как рывок.
        visibility: pos ? "visible" : "hidden",
        // Растёт от кнопки: X — её центр, Y — ближний к ней край панели.
        transformOrigin: pos ? `${pos.originX}px ${pos.below ? "0%" : "100%"}` : undefined,
        ["--op-pop-y" as string]: pos && !pos.below ? "4px" : "-4px",
      }}
      data-state={closing ? "closing" : "open"}
      role="dialog"
      aria-label={label}
    >
      {children}
    </div>,
    host,
  );
}

/** Что Overlay отдаёт вниз: закрываемся ли, и как попросить закрыться с анимацией. */
const OverlayContext = createContext<{ closing: boolean; requestClose: () => void } | null>(null);

/** Кнопка «Закрыть» внутри окна — закрывает С ВЫХОДОМ (через Overlay), а не кадром. */
export function OverlayCloseButton({ className, children }: { className?: string; children: ReactNode }) {
  const ov = useContext(OverlayContext);
  return (
    <button type="button" className={className} onClick={() => ov?.requestClose()}>
      {children}
    </button>
  );
}

const OVERLAY_EXIT_MS = 150;

export function Overlay({
  children,
  onClose,
  label,
}: {
  children: ReactNode;
  onClose: () => void;
  label?: string;
}) {
  // Портал только после монтирования: на сервере `document` нет, а рисовать
  // разметку, которой не будет в браузере, значит получить рассинхрон гидратации.
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [closing, setClosing] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    // Целимся в КОРЕНЬ ТЕМЫ, а не в `document.body`. Все токены `--op-*`
    // объявлены на `.op-root`; за его пределами `var(--op-surface)` не
    // существует, и фон окна схлопывается в прозрачный — окно выглядит как
    // текст, висящий на затемнении. `body` оставлен запасным вариантом на
    // случай, если разметка когда-нибудь поменяется.
    setHost(document.querySelector<HTMLElement>(".op-root") ?? document.body);
  }, []);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  // Сначала выход (150ms), потом onClose: родитель размонтирует уже пустое место.
  // Повторный запрос во время выхода игнорируется.
  const requestClose = useCallback(() => {
    if (timer.current) return;
    setClosing(true);
    timer.current = setTimeout(onClose, OVERLAY_EXIT_MS + 10);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestClose]);

  if (!host) return null;

  return createPortal(
    <div
      className="op-dim fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/45 p-4 backdrop-blur-[2px] sm:p-8"
      data-state={closing ? "closing" : "open"}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onMouseDown={(e) => {
        // Именно mouseDown на самом фоне: click срабатывал бы и тогда, когда
        // выделение текста началось внутри окна, а отпустили мышь снаружи.
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <OverlayContext.Provider value={{ closing, requestClose }}>{children}</OverlayContext.Provider>
    </div>,
    host,
  );
}

export function Spinner({ text = "Загружаю…" }: { text?: string }) {
  return (
    <div className="flex items-center gap-2 py-6 text-[13px] text-[var(--op-muted)]">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--op-line-strong)] border-t-[var(--op-accent)]" />
      {text}
    </div>
  );
}

export function ErrorNote({ text }: { text: string }) {
  return (
    <div className="rounded-2xl bg-[var(--op-danger-soft)] px-3.5 py-2.5 text-[13px] text-[var(--op-danger-text)]">
      {text}
    </div>
  );
}

/**
 * Ответ на удачное действие (09.09.2026).
 *
 * Появился по заказу Марата: форма постановки задачи на успех просто
 * очищалась, и очистка читалась одинаково и как «поставилась», и как
 * «сбросилось». Отдельный вид, а не `ErrorNote` с другим цветом: сюда попадает
 * и «поставлено, но в мессенджер не ушло» — это не ошибка, красная рамка над
 * созданной задачей врала бы.
 */
export function OkNote({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded-2xl bg-[var(--op-surface-2)] px-3.5 py-2.5 text-[13px] text-[var(--op-fg)]">
      <span className="mt-[3px] flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[var(--op-lime)] text-white">
        <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      </span>
      <span>{text}</span>
    </div>
  );
}

export function Empty({ text }: { text: string }) {
  return <div className="py-8 text-center text-[13px] text-[var(--op-faint)]">{text}</div>;
}

// Модальное окно поверх экрана.
//
// Правка Марата 01.08: правка ставок роли не должна уводить с экрана —
// уходя на отдельную вкладку, человек теряет из виду остальные роли, а
// сравнение «сколько платим бармену против официанта» и есть смысл витрины.
//
// Закрывается по фону и по Escape — оба ожидаемы; крестик оставлен для мыши.
// Прокрутка внутри окна, а не страницы: длинная форма не должна утаскивать
// фон, иначе после закрытия человек оказывается не там, где был.
export function Modal({
  title,
  subtitle,
  onClose,
  footer,
  children,
  width = "760px",
  originRef,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  width?: string;
  /** Кнопка-триггер (N7-А, находка #4): если передана, окно растёт из нЕё, а
   * не из центра — единственное место, где у модалки есть реальный триггер
   * на экране (`CollapsedTable`, круглая кнопка «Развернуть»). */
  originRef?: React.RefObject<HTMLElement | null>;
}) {
  // Escape и клик по фону живут в `Overlay` — он же уносит слой порталом
  // из-под размытых предков (см. комментарий к `Overlay`) и играет выход.
  return (
    <Overlay onClose={onClose}>
      <ModalFrame title={title} subtitle={subtitle} footer={footer} width={width} originRef={originRef}>
        {children}
      </ModalFrame>
    </Overlay>
  );
}

function ModalFrame({
  title,
  subtitle,
  footer,
  width,
  children,
  originRef,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  width: string;
  children: ReactNode;
  originRef?: React.RefObject<HTMLElement | null>;
}) {
  const ov = useContext(OverlayContext);
  const frameRef = useRef<HTMLDivElement>(null);
  const [origin, setOrigin] = useState<{ x: number; y: number } | null>(null);
  // Замер — синхронно ДО кадра (как в Popover, см. его комментарий): иначе
  // первый кадр окно ещё растёт из центра, и переключение origin читается
  // рывком поверх собственного входа.
  useLayoutEffect(() => {
    const btn = originRef?.current?.getBoundingClientRect();
    const frame = frameRef.current?.getBoundingClientRect();
    if (!btn || !frame) {
      setOrigin(null);
      return;
    }
    setOrigin({ x: btn.left + btn.width / 2 - frame.left, y: btn.top + btn.height / 2 - frame.top });
  }, [originRef]);
  return (
    // Окно ПОДНИМАЕТСЯ и доезжает до масштаба (`.op-modal`, 200мс вход / 150мс
    // выход): раньше оно возникало и пропадало кадром, и это читалось как
    // рывок посреди экрана. Из центра, а не от кнопки: у модалки триггера на
    // экране обычно нет — `originRef` задаёт исключение (N7-А, #4).
    <div
      ref={frameRef}
      className="op-modal my-auto w-full rounded-2xl bg-[var(--op-surface)] [box-shadow:var(--op-shadow-lg)]"
      data-state={ov?.closing ? "closing" : "open"}
      data-origin={origin ? "button" : undefined}
      style={{ maxWidth: width, transformOrigin: origin ? `${origin.x}px ${origin.y}px` : undefined }}
    >
      <header className="flex items-start justify-between gap-3 border-b border-[var(--op-line)] px-5 py-4">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-[var(--op-fg)]">{title}</h2>
          {subtitle && <div className="mt-0.5 text-xs text-[var(--op-faint)]">{subtitle}</div>}
        </div>
        <button
          type="button"
          onClick={() => ov?.requestClose()}
          aria-label="Закрыть"
          className="group grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[var(--op-faint)] transition-colors hover:bg-[var(--op-ghost)] hover:text-[var(--op-fg)]"
        >
          <CloseIcon className={`${motionIcon} h-4 w-4 group-hover:rotate-90`} />
        </button>
      </header>
      <div className="max-h-[calc(100vh-14rem)] overflow-y-auto overscroll-contain px-5 py-4">{children}</div>
      {footer && (
        <footer className="flex flex-wrap justify-end gap-2 border-t border-[var(--op-line)] px-5 py-4">
          {footer}
        </footer>
      )}
    </div>
  );
}

// Инициалы в кружке — компактная замена аватарке в плотной сетке календаря,
// где фотография 24×24 всё равно неразличима.
export function Avatar({ name, avatar, size = 24 }: { name: string; avatar?: string | null; size?: number }) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
  if (avatar) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={avatar}
        alt={name}
        title={name}
        width={size}
        height={size}
        // Подложка: логотип заведения, стоящий аватаром по умолчанию, бывает
        // любого цвета и на однотонном фоне пропадает.
        className="shrink-0 rounded-full border border-[var(--op-line)] bg-[var(--op-surface-2)] object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      title={name}
      className="grid shrink-0 place-items-center rounded-full bg-[var(--op-ghost-hover)] font-semibold text-[var(--op-muted)]"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {initials}
    </span>
  );
}

/* ═══ Общие кирпичи для аналитики и списков (заход «Сезон», 18.09) ═══
   Пять примитивов ниже нужны сразу нескольким разделам (Аналитика, Задачи,
   Ликвидность, ABC) — заведены один раз здесь, а не в каждом разделе заново,
   чтобы плашка статуса или сворачиваемая таблица не разъехались по вёрстке
   от экрана к экрану. */

export type StatusLozTone = "new" | "progress" | "overdue" | "problem" | "done";

// «new»/«progress» — служебные нейтральные цвета, ВНЕ правила четырёх
// смыслов (globals.css): это состояние очереди, а не «хорошо/плохо/внимание».
const STATUS_LOZ_TONE: Record<StatusLozTone, string> = {
  new: "bg-[#ececea] text-[#4a4f55]",
  progress: "bg-[#e3ecf9] text-[#1f4e9c]",
  overdue: "bg-[var(--op-bad-soft)] text-[var(--op-bad)]",
  problem: "bg-[var(--op-attn-soft)] text-[var(--op-attn)]",
  done: "bg-[var(--op-good-soft)] text-[var(--op-good)]",
};

/**
 * Плашка статуса задачи — «лозенж», как в Jira (13c/22a).
 *
 * ШИРИНА ЗАФИКСИРОВАНА (108px), а НЕ по длине текста (Марат: «пилюли не
 * должны скакать по длине») — иначе «Готово» и «Просрочено» в одном списке
 * стоят плашками разной ширины, и колонка статусов перестаёт читаться
 * вертикальной линией. Форма — НЕ пилюля: лёгкое скругление r=6, как у
 * прочих мелких служебных меток (18c) — пилюля в этой системе означает
 * «фильтр/вкладка», а статус задачи им не является.
 */
export function StatusLoz({ tone, children }: { tone: StatusLozTone; children: ReactNode }) {
  return (
    <span
      // Тон меняется ПЛАВНО (N7-А, ревью 18.09): было — new→progress→done
      // перекрашивались кадром на «Начать»/переносе карточки на доске.
      className={`inline-flex h-6 w-[108px] shrink-0 items-center justify-center rounded-[6px] text-[11px] font-semibold transition-[background-color,color] duration-200 ease-[var(--ease-out)] ${STATUS_LOZ_TONE[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * Значок-подсказка ⓘ рядом с заголовком карточки (16a).
 *
 * Раньше пояснение к показателю жило отдельным абзацем под заголовком —
 * абзац читали один раз, а дальше он просто занимал место на каждом заходе
 * на экран. Здесь тот же текст спрятан за значком и открывается ЛЮБЫМ из
 * трёх способов до него добраться — наведением, клавиатурным фокусом и
 * кликом, — чтобы мышь, тачпад и Tab давали одинаковый результат.
 */
/**
 * Знак вопроса с пояснением — один на весь десктоп.
 *
 * Заказ пользователя 22.09: «в таких местах ставить маленький знак вопроса,
 * при наведении на который будет пояснение, для чего этот функционал; чтобы
 * это было красиво, в тему и не мешалось особо».
 *
 * Отсюда три правила, которые важнее кода:
 *
 *  1. Знак вопроса, а не «i». Кружок с «i» читается как «справка вообще», знак
 *     вопроса — как ответ на конкретный вопрос «а это что?», который человек
 *     в этот момент и задаёт.
 *  2. 16 пикселей, цвет служебных значков, БЕЗ фона и рамки. Он стоит рядом с
 *     подписью поля и не должен спорить с ней за внимание: его замечают,
 *     только когда ищут.
 *  3. Вешать на то, что НЕ объясняется само. Знак вопроса у поля «Имя» — шум,
 *     который обесценивает знак вопроса у поля «Пур-кост».
 */
export function InfoTip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <span className="relative inline-flex">
      <button
        ref={ref}
        type="button"
        aria-label="Что это"
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        /* Только открывает (M2, ревью «Сезон»): раньше клик ПЕРЕКЛЮЧАЛ
           состояние, и на тач-логике (mouseenter → focus → click подряд от
           одного тапа) второе событие сразу гасило то, что включило первое.
           Закрывает Popover сам — по клику вне или Escape. */
        onClick={() => setOpen(true)}
        className="grid h-4 w-4 shrink-0 place-items-center rounded-full text-[var(--op-faint)] outline-none transition-colors hover:text-[var(--op-muted)] focus-visible:ring-2 focus-visible:ring-[var(--op-ring)]"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.4 9.3a2.7 2.7 0 015.2.9c0 1.8-2.6 2.6-2.6 2.6" />
          <path d="M12 16.6v.01" />
        </svg>
      </button>
      <Popover open={open} anchorRef={ref} onClose={() => setOpen(false)} width={280} label="Подсказка">
        <p className="text-[12px] leading-snug text-[var(--op-muted)]">{text}</p>
      </Popover>
    </span>
  );
}

/**
 * Карточка с длинной таблицей, свёрнутой до первых строк (16b).
 *
 * Таблица на 40+ строк топит собой карточку и вместе с ней весь раздел — до
 * следующего блока приходится листать экран. Показываем только первые
 * `maxInline`, а полный список — по круглой кнопке в углу, в отдельном окне,
 * где для него есть вся высота экрана и своя прокрутка.
 *
 * `renderTable` вызывается ДВАЖДЫ — на усечённых строках и на полных: это та
 * же функция отрисовки, что использует вызывающий раздел, поэтому колонки и
 * форматирование в карточке и в окне не могут разъехаться между собой.
 */
export function CollapsedTable<T>({
  title,
  right,
  rows,
  renderTable,
  maxInline = 10,
  modalWidth = "960px",
}: {
  title: ReactNode;
  /** Доп. содержимое в шапке карточки (например поиск) — рядом с кнопкой развёрнуть. */
  right?: ReactNode;
  rows: T[];
  renderTable: (rows: T[]) => ReactNode;
  maxInline?: number;
  modalWidth?: string;
}) {
  const [open, setOpen] = useState(false);
  // Триггер модалки (N7-А, находка #4): окно растёт из этой кнопки, а не из
  // центра — здесь, в отличие от большинства модалок, есть реальный триггер.
  const expandRef = useRef<HTMLButtonElement>(null);
  const hasMore = rows.length > maxInline;
  const shown = hasMore ? rows.slice(0, maxInline) : rows;
  // `right` в Card только когда есть что показать (L6, ревью «Сезон»): пустой
  // `<div>` без содержимого раньше всё равно резервировал место в шапке.
  const headerRight =
    right || hasMore ? (
      <div className="flex shrink-0 items-center gap-2">
        {right}
        {hasMore && (
          <button
            ref={expandRef}
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Развернуть таблицу целиком"
            title="Развернуть"
            className="op-hover-soft op-raise grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--op-surface)] text-[var(--op-muted)] transition-colors hover:text-[var(--op-fg)]"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
            </svg>
          </button>
        )}
      </div>
    ) : undefined;
  return (
    <Card
      // Пустой заголовок не рендерит пустую шапку (L6): `Card` сама решает,
      // показывать ли строку заголовка, по `title`/`right` — здесь только не
      // мешаем ей, передавая `undefined`, а не пустую строку/элемент.
      title={title || undefined}
      right={headerRight}
    >
      {hasMore && (
        <p className="text-[12px] text-[var(--op-faint)]">
          показаны {maxInline} из {rows.length}
        </p>
      )}
      {renderTable(shown)}
      {open && (
        <Modal title={title} onClose={() => setOpen(false)} width={modalWidth} originRef={expandRef}>
          {renderTable(rows)}
        </Modal>
      )}
    </Card>
  );
}

/**
 * Полоса источника данных — например, «Касса iiko подключена» над ABC (19a).
 *
 * `tone="warn"` красится ФИОЛЕТОВЫМ (`--op-attn`), а не жёлто-оранжевым и не
 * красным: по правилу четырёх смыслов (globals.css) это «требует внимания»,
 * а не ошибка — данные всё равно есть, просто не из кассы. `--op-bad` здесь
 * был бы враньём: ничего не сломано.
 */
export function SourceStrip({
  tone,
  title,
  sub,
  right,
}: {
  tone: "ok" | "warn";
  title: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
}) {
  const isOk = tone === "ok";
  return (
    <div
      className={`flex flex-wrap items-center gap-3 rounded-2xl px-4 py-3 ${isOk ? "bg-[var(--op-good-soft)]" : "bg-[var(--op-attn-soft)]"}`}
    >
      <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${isOk ? "bg-[var(--op-good)]" : "bg-[var(--op-attn)]"}`} />
      <div className="min-w-0 flex-1">
        <p className={`text-[13px] font-semibold ${isOk ? "text-[var(--op-good)]" : "text-[var(--op-attn)]"}`}>{title}</p>
        {sub && <p className="mt-0.5 text-[12px] text-[var(--op-muted)]">{sub}</p>}
      </div>
      {right && <div className="flex shrink-0 items-center gap-2">{right}</div>}
    </div>
  );
}

/**
 * Сегментированный прогресс — для чек-листов («2 из 6», 28a).
 *
 * Отдельные прямоугольники, а не одна заливающаяся полоска: по пунктам
 * чек-листа считают глазами «сколько ещё», и дискретные сегменты честно это
 * показывают — на 2 из 6 непрерывная полоса выглядела бы как «почти пусто».
 */
export function Segments({
  total,
  done,
  tone,
}: {
  total: number;
  done: number;
  tone: "ok" | "bad" | "accent";
}) {
  // «Идёт» — тёмно-серым, не оранжевым (S1-А: оранжевый — только действие).
  const toneClass = tone === "ok" ? "bg-[var(--op-good)]" : tone === "bad" ? "bg-[var(--op-bad)]" : "bg-[var(--op-muted)]";
  // Лесенка только для сегментов, заполнившихся С ПРОШЛОГО РЕНДЕРА (N7-А,
  // ревью 18.09): без этого «2 из 6» перекрашивалась бы целиком при каждой
  // отрисовке, а не только вновь отмеченный пункт. Потолок 5 ступеней —
  // глубже задержка на 200мс-переходе уже не читается.
  const prevDone = useRef(done);
  const from = prevDone.current;
  useEffect(() => {
    prevDone.current = done;
  }, [done]);
  return (
    // flex-wrap (L5, ревью «Сезон»): чек-лист на полсотни пунктов раньше
    // сжимал сегменты в одну нечитаемую полоску шириной карточки — теперь
    // лишние переносятся строкой ниже, каждый сегмент остаётся своего размера.
    <div className="flex flex-wrap items-center gap-[3px]" role="img" aria-label={`Выполнено ${done} из ${total}`}>
      {Array.from({ length: Math.max(total, 0) }).map((_, i) => {
        const justFilled = i < done && i >= from;
        return (
          <span
            key={i}
            aria-hidden
            className={`h-[6px] w-[14px] shrink-0 rounded-[2px] transition-colors duration-200 ease-[var(--ease-out)] ${i < done ? toneClass : "bg-[var(--op-ghost)]"}`}
            style={justFilled ? { transitionDelay: `${Math.min(i - from, 4) * 40}ms` } : undefined}
          />
        );
      })}
    </div>
  );
}
